import { useRef, useState, useCallback, useEffect } from "react";
import "./index.css";

const CONSENT_KEY = "cookie-consent";
const WELCOME_KEY = "welcome-seen";

const CATEGORIES = [
  "ANIMAL",
  "PLACE",
  "PLANT",
  "CHARACTER",
  "SEASON",
  "HOBBY",
  "COLOR",
  "DRINK",
  "FOOD",
] as const;

type Category = (typeof CATEGORIES)[number];
type ImageMap = Partial<Record<Category, string>>;
type Template = "portrait" | "square";

const PORTRAIT_EXPORT_WIDTH = 1080;
const PORTRAIT_EXPORT_HEIGHT = 1920; // 9:16 portrait ratio
const SQUARE_EXPORT_SIZE = 1080; // 1:1 square ratio

export function App() {
  const [images, setImages] = useState<ImageMap>({});
  const [template, setTemplate] = useState<Template>("portrait");
  const [showWelcome, setShowWelcome] = useState(false);
  const [cookieConsent, setCookieConsent] = useState<
    "accepted" | "declined" | null
  >(null);
  const inputRefs = useRef<Record<Category, HTMLInputElement | null>>(
    {} as Record<Category, HTMLInputElement | null>
  );

  const allFilled = CATEGORIES.every((cat) => images[cat]);

  // Check for existing cookie consent and welcome on mount
  useEffect(() => {
    const consentStored = localStorage.getItem(CONSENT_KEY);
    if (consentStored === "accepted" || consentStored === "declined") {
      setCookieConsent(consentStored);
    }

    const welcomeSeen = localStorage.getItem(WELCOME_KEY);
    if (!welcomeSeen) {
      setShowWelcome(true);
    }
  }, []);

  const handleCloseWelcome = useCallback(() => {
    localStorage.setItem(WELCOME_KEY, "true");
    setShowWelcome(false);
  }, []);

  const handleCookieAccept = useCallback(() => {
    localStorage.setItem(CONSENT_KEY, "accepted");
    setCookieConsent("accepted");
  }, []);

  const handleCookieDecline = useCallback(() => {
    localStorage.setItem(CONSENT_KEY, "declined");
    setCookieConsent("declined");
  }, []);

  const handleCellClick = useCallback((category: Category) => {
    inputRefs.current[category]?.click();
  }, []);

  const handleFileChange = useCallback(
    (category: Category, e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;

      const reader = new FileReader();
      reader.onload = (event) => {
        const dataUrl = event.target?.result as string;
        setImages((prev) => ({ ...prev, [category]: dataUrl }));
      };
      reader.readAsDataURL(file);

      // Reset input so same file can be re-selected
      e.target.value = "";
    },
    []
  );

  const handleDownload = useCallback(async () => {
    const isSquare = template === "square";
    const exportWidth = isSquare ? SQUARE_EXPORT_SIZE : PORTRAIT_EXPORT_WIDTH;
    const exportHeight = isSquare ? SQUARE_EXPORT_SIZE : PORTRAIT_EXPORT_HEIGHT;

    const canvas = document.createElement("canvas");
    canvas.width = exportWidth;
    canvas.height = exportHeight;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const cellWidth = exportWidth / 3;
    const cellHeight = exportHeight / 3;
    const gap = 2;

    // Fill background black (for grid lines)
    ctx.fillStyle = "#000";
    ctx.fillRect(0, 0, exportWidth, exportHeight);

    // Load all images first
    const loadImage = (src: string): Promise<HTMLImageElement> => {
      return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = reject;
        img.src = src;
      });
    };

    const imagePromises = CATEGORIES.map((cat) => {
      const src = images[cat];
      return src ? loadImage(src) : Promise.resolve(null);
    });

    const loadedImages = await Promise.all(imagePromises);

    // Draw each image
    loadedImages.forEach((img, index) => {
      if (!img) return;

      const row = Math.floor(index / 3);
      const col = index % 3;
      const x = col * cellWidth + (col > 0 ? gap : 0);
      const y = row * cellHeight + (row > 0 ? gap : 0);
      const w = cellWidth - (col > 0 ? gap : 0);
      const h = cellHeight - (row > 0 ? gap : 0);

      // Cover fit - center crop
      const imgAspect = img.width / img.height;
      const cellAspect = w / h;

      let sx = 0,
        sy = 0,
        sw = img.width,
        sh = img.height;

      if (imgAspect > cellAspect) {
        // Image is wider - crop sides
        sw = img.height * cellAspect;
        sx = (img.width - sw) / 2;
      } else {
        // Image is taller - crop top/bottom
        sh = img.width / cellAspect;
        sy = (img.height - sh) / 2;
      }

      ctx.drawImage(img, sx, sy, sw, sh, x, y, w, h);
    });

    // Draw grid lines
    ctx.strokeStyle = "#000";
    ctx.lineWidth = gap;

    // Vertical lines
    for (let i = 1; i < 3; i++) {
      ctx.beginPath();
      ctx.moveTo(i * cellWidth, 0);
      ctx.lineTo(i * cellWidth, exportHeight);
      ctx.stroke();
    }

    // Horizontal lines
    for (let i = 1; i < 3; i++) {
      ctx.beginPath();
      ctx.moveTo(0, i * cellHeight);
      ctx.lineTo(exportWidth, i * cellHeight);
      ctx.stroke();
    }

    // Watermark
    const watermarkText = "made with ❤️ on hisyhysm.peslar.com";
    const watermarkX = exportWidth - 16;
    const watermarkY = exportHeight - 12;
    ctx.font = "20px Helvetica, Arial, sans-serif";
    ctx.textAlign = "right";
    ctx.textBaseline = "bottom";
    // Shadow/outline for visibility on any background
    ctx.strokeStyle = "rgba(0, 0, 0, 0.5)";
    ctx.lineWidth = 3;
    ctx.strokeText(watermarkText, watermarkX, watermarkY);
    // White fill
    ctx.fillStyle = "rgba(255, 255, 255, 0.9)";
    ctx.fillText(watermarkText, watermarkX, watermarkY);

    // Download
    canvas.toBlob((blob) => {
      if (!blob) return;
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "how-i-see-you-how-you-see-me.png";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }, "image/png");
  }, [images, template]);

  return (
    <div className="app-container">
      <div className="title-row">
        <button
          className="template-toggle"
          onClick={() =>
            setTemplate((prev) => (prev === "portrait" ? "square" : "portrait"))
          }
          aria-label={`Switch to ${
            template === "portrait" ? "square" : "portrait"
          } template`}
          title={`Switch to ${
            template === "portrait" ? "square" : "portrait"
          } template`}
        >
          {template === "portrait" ? (
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <rect x="3" y="3" width="18" height="18" rx="2" />
            </svg>
          ) : (
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <rect x="5" y="2" width="14" height="20" rx="2" />
            </svg>
          )}
        </button>
        <h1 className="title">How I See You, How You See Me</h1>
      </div>

      <div
        className={`grid-container ${
          template === "square" ? "grid-square" : "grid-portrait"
        }`}
      >
        {CATEGORIES.map((category) => {
          const hasImage = !!images[category];
          return (
            <div
              key={category}
              className={`grid-cell ${hasImage ? "has-image" : ""}`}
              onClick={() => handleCellClick(category)}
            >
              {hasImage ? (
                <>
                  <img
                    src={images[category]}
                    alt={category}
                    className="cell-image"
                  />
                  <div className="cell-overlay">
                    <span className="cell-overlay-text">
                      Change
                      <br />
                      {category}
                    </span>
                  </div>
                </>
              ) : (
                <span className="cell-label">{category}</span>
              )}
              <input
                ref={(el) => {
                  inputRefs.current[category] = el;
                }}
                type="file"
                accept="image/*"
                className="hidden-input"
                onChange={(e) => handleFileChange(category, e)}
              />
            </div>
          );
        })}
      </div>

      <div className="button-row">
        <button
          className="download-btn"
          onClick={handleDownload}
          disabled={!allFilled}
        >
          Download
        </button>
        <button
          className="info-btn"
          onClick={() => setShowWelcome(true)}
          aria-label="Show info"
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="16" x2="12" y2="12" />
            <line x1="12" y1="8" x2="12.01" y2="8" />
          </svg>
        </button>
      </div>

      {showWelcome && (
        <div className="modal-overlay" onClick={handleCloseWelcome}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2 className="modal-title">Welcome!</h2>
            <p className="modal-text">
              Your photos stay completely private. Nothing leaves your device —
              no uploads, no servers, no data collection. Everything happens
              right here in your browser.
            </p>
            <p className="modal-text">
              Just pick your images, create your grid, and download it. Simple
              as that!
            </p>
            <button className="modal-btn" onClick={handleCloseWelcome}>
              Got it!
            </button>
          </div>
        </div>
      )}

      {cookieConsent === null && (
        <div className="cookie-banner">
          <p className="cookie-text">
            We use cookies to understand how visitors use this site. Your photos
            are never collected.
          </p>
          <div className="cookie-buttons">
            <button
              className="cookie-btn cookie-btn-accept"
              onClick={handleCookieAccept}
            >
              Accept
            </button>
            <button
              className="cookie-btn cookie-btn-decline"
              onClick={handleCookieDecline}
            >
              Decline
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default App;
